import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaCinco(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11)); // landscape-ish
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // split ratio (left gray / right white)
      const leftRatio = typeof opts.leftRatio === 'number' ? opts.leftRatio : 0.30;
      const leftW = Math.round(width * leftRatio);
      const rightX = leftW;

      // left: gray
      ctx.fillStyle = opts.leftBg || '#ffffff';
      ctx.fillRect(0, 0, leftW, height);

      // right: white
      ctx.fillStyle = opts.rightBg || '#ffffff';
      ctx.fillRect(rightX, 0, width - rightX, height);

      // subtle divider
      ctx.fillStyle = opts.dividerColor || '#ffffff';
      ctx.fillRect(Math.max(0, rightX - 1), 16, 2, height - 32);

      // --- content on left (gray) side ---
      const leftPadding = typeof opts.leftPadding === 'number' ? opts.leftPadding : 32;
      let leftContentX = leftPadding;
      let leftContentY = 100;
      // draw square number (like "1.1") at the very left of the gray side and shift content right
      const squareSize = typeof opts.leftSquareSize === 'number' ? opts.leftSquareSize : 70;
      const sqX = leftPadding;
      const sqY = leftContentY;
      // square background
      ctx.save();
      ctx.fillStyle = opts.leftSquareBg || '#5fe7ffff';
      ctx.fillRect(sqX, sqY, squareSize, squareSize);
      // square number text
      ctx.fillStyle = opts.leftSquareColor || '#01090cff';
      ctx.font = `bold ${opts.leftSquareFontSize || 40}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(opts.leftSquareText || '1.2', sqX + squareSize / 2, sqY + squareSize / 2);
      ctx.restore();
      // move content start to the right of the square (plus a small gap)
      leftContentX = sqX + squareSize + (typeof opts.leftSquareGap === 'number' ? opts.leftSquareGap : 12);
      const leftMaxW = leftW - leftContentX - leftPadding;

      const wrapText = (c, text, x, y0, maxWidth, lh) => {
        const words = String(text).split(' ').filter(Boolean);
        if (words.length === 0) return y0 + lh;
        
        const lines = [];
        let line = [];
        for (let i = 0; i < words.length; i++) {
          const testLine = [...line, words[i]];
          const testText = testLine.join(' ');
          if (c.measureText(testText).width > maxWidth && line.length > 0) {
            lines.push(line);
            line = [words[i]];
          } else {
            line = testLine;
          }
        }
        if (line.length > 0) lines.push(line);

        let yPos = y0;
        for (let li = 0; li < lines.length; li++) {
          const lineWords = lines[li];
          const isLastLine = li === lines.length - 1;
          
          if (isLastLine || lineWords.length === 1) {
            c.fillText(lineWords.join(' '), x, yPos);
          } else {
            const lineText = lineWords.join(' ');
            const lineWidth = c.measureText(lineText).width;
            const extraSpace = maxWidth - lineWidth;
            const gaps = lineWords.length - 1;
            const spacePerGap = gaps > 0 ? extraSpace / gaps : 0;
            
            let xPos = x;
            for (let wi = 0; wi < lineWords.length; wi++) {
              c.fillText(lineWords[wi], xPos, yPos);
              if (wi < lineWords.length - 1) {
                const wordWidth = c.measureText(lineWords[wi]).width;
                const spaceWidth = c.measureText(' ').width;
                xPos += wordWidth + spaceWidth + spacePerGap;
              }
            }
          }
          yPos += lh;
        }
        return yPos;
      };

      // Provided content (prefer opts, then props, then defaults)
      const title = opts.title || props.title || 'Recepción de población desplazada proveniente de OTROS DEPARTAMENTOS';
      const subTitle = opts.subTitle || props.subTitle || '';
      const textoUno = opts.textoUno || props.textoUno || 'Tabla 2. Principales departamentos expulsores de población desplazada con recepción en Medellín, semana actual.';
      const textoDos = opts.textoDos || props.textoDos || '';

      // title — span across both left and right sides of canvas
      const rightPadding = typeof opts.rightPadding === 'number' ? opts.rightPadding : 32;
      const titleMaxW = width - leftContentX - rightPadding;
      const titleSize = (typeof opts.leftTitleSize === 'number')
        ? opts.leftTitleSize
        : 40;
      ctx.fillStyle = opts.leftTitleColor || '#0f172a';
      ctx.font = `bold ${titleSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      leftContentY = wrapText(ctx, title, leftContentX, leftContentY, titleMaxW, Math.round(titleSize * 1.4));
      
      // save position after title for right content
      const afterTitleY = leftContentY + 50;

      // subtitle (smaller, muted)
      ctx.fillStyle = opts.leftSubTitleColor || '#475569';
      ctx.font = `${opts.leftSubTitleSize || 40}px Inter, Arial, sans-serif`;
      leftContentY = wrapText(ctx, subTitle, leftContentX, leftContentY + 8, leftMaxW, Math.round((opts.leftSubTitleSize || 40) * 1.4));

      // spacer before paragraphs
      leftContentY += 12;

      // paragraph textoUno
      ctx.fillStyle = opts.leftTextColor || '#334155';
      ctx.font = `${opts.leftTextSize || 25}px Inter, Arial, sans-serif`;
      leftContentY = wrapText(ctx, textoUno, leftContentX, leftContentY + 40, leftMaxW, Math.round((opts.leftTextSize || 25) * 1.6));

      // small note textoDos (muted) - keep original textoDos
      ctx.fillStyle = opts.leftNoteColor || '#475569';
      ctx.font = `${opts.leftNoteSize || 25}px Inter, Arial, sans-serif`;
      leftContentY = wrapText(ctx, textoDos, leftContentX, leftContentY + 100, leftMaxW, Math.round((opts.leftNoteSize || 25) * 1.6));

      // group tableData early to generate otros departamentos text
      const tableData = opts.tableData || props.tableData || [];
      const groupMapEarly = {};
      for (let i = 0; i < tableData.length; i++) {
        const r = tableData[i] || {};
        const dep = String(r.departamento || '').trim();
        const muni = String(r.municipio || '').trim();
        const totalHog = parseInt(String(r.totalHogares || '0').replace(/\D/g, ''), 10) || 0;
        const totalPers = parseInt(String(r.totalPersonas || '0').replace(/\D/g, ''), 10) || 0;
        
        if (!groupMapEarly[dep]) groupMapEarly[dep] = { departamento: dep, municipios: [] };
        groupMapEarly[dep].municipios.push({ municipio: muni, totalHogares: totalHog, totalPersonas: totalPers });
      }

      const groupedEarly = Object.keys(groupMapEarly)
        .map(k => ({
          departamento: groupMapEarly[k].departamento,
          totalPersonas: groupMapEarly[k].municipios.reduce((sum, m) => sum + m.totalPersonas, 0),
          totalHogares: groupMapEarly[k].municipios.reduce((sum, m) => sum + m.totalHogares, 0)
        }))
        .sort((a, b) => b.totalHogares - a.totalHogares);

      const remainingGroupsEarly = groupedEarly.slice(5);
      if (remainingGroupsEarly.length > 0) {
        const totalHogaresOtros = remainingGroupsEarly.reduce((sum, g) => sum + g.totalHogares, 0);
        const totalPersonasOtros = remainingGroupsEarly.reduce((sum, g) => sum + g.totalPersonas, 0);
        const deptNames = remainingGroupsEarly.map(g => g.departamento).join(', ');
        const otrosDepartamentosText = `*Otros departamentos: en la semana actual se recibieron, en menor intensidad, hogares de ${deptNames} (Total: ${totalHogaresOtros} hogares, ${totalPersonasOtros} personas)`;
        
        ctx.fillStyle = opts.leftNoteColor || '#475569';
        ctx.font = `${opts.leftNoteSize || 25}px Inter, Arial, sans-serif`;
        leftContentY = wrapText(ctx, otrosDepartamentosText, leftContentX, leftContentY + 12, leftMaxW, Math.round((opts.leftNoteSize || 25) * 1.6));
      }

      const mainLogoSrc = opts.logoSrc || logoSrc;
      const logoPromises = [];

      // existing bottom-right logo (if provided) — load later together with left logo
      const mainLogoPromise = mainLogoSrc
        ? new Promise((res) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try {
                const maxW = Math.min(200, Math.round(width * 0.18));
                const w = Math.min(maxW, img.width);
                const h = (img.height / img.width) * w;
                const lx = width - 16 - w;
                const ly = height - 16 - h;
                ctx.drawImage(img, lx, ly, w, h);
              } catch (e) { /* ignore */ }
              res();
            };
            img.onerror = () => res();
            img.src = mainLogoSrc;
          })
        : Promise.resolve();
      logoPromises.push(mainLogoPromise);

      // --- table: three columns on the RIGHT (Departamento | Municipio | Total hogares) ---
      // right side content area — start after title
      const rightContentX = rightX + rightPadding;
      const rightContentY = afterTitleY + 20; // start after title with some spacing
      const rightMaxW = (width - rightX) - rightPadding * 2;

      // allocate a narrow summary column on the far right and assign remaining width to the table
      const summaryColW = typeof opts.summaryColumnWidth === 'number' ? opts.summaryColumnWidth : 220;
      const summaryColGap = typeof opts.summaryColumnGap === 'number' ? opts.summaryColumnGap : 12;
      const tableAreaW = Math.max(200, rightMaxW - summaryColW - summaryColGap);
      const tableX = rightContentX;
      const cardsX = tableX + tableAreaW + summaryColGap;
      const cardsColumnWidth = summaryColW;
      // table start Y
      const tableY = rightContentY + 12;

      // table layout on right (use tableAreaW)
      const rowH = Math.round((opts.tableRowHeight || 28));
      const headerH = Math.round(rowH * 1.1);
      // columns based on tableAreaW: 35% | 30% | 17.5% | 17.5%
      const col1R = Math.floor(tableAreaW * 0.30); // Departamento
      const col2R = Math.floor(tableAreaW * 0.35); // Municipio
      const col3R = Math.floor(tableAreaW * 0.175); // Total hogares
      const col4R = tableAreaW - col1R - col2R - col3R; // Total personas

      // header (on white side) — add left padding for departamento column to avoid clipping
      const depPadding = typeof opts.tableDepartamentoPadding === 'number' ? opts.tableDepartamentoPadding : 12;
      ctx.save();
      ctx.fillStyle = opts.tableHeaderBg || '#0f172a';
      ctx.fillRect(tableX, tableY, tableAreaW, headerH);
      ctx.fillStyle = opts.tableHeaderColor || '#ffffff';
      ctx.font = `${opts.tableHeaderSize || 20}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const hh = tableY + Math.round(headerH / 2);
      ctx.fillText('Departamento', tableX + depPadding, hh);
      ctx.fillText('Municipio', tableX + col1R + depPadding, hh);
      // center headers for total columns
      ctx.textAlign = 'center';
      const hogaresColCenterX = tableX + col1R + col2R + Math.round(col3R / 2);
      const personasColCenterX = tableX + col1R + col2R + col3R + Math.round(col4R / 2);
      ctx.fillText('Hogares', hogaresColCenterX, hh);
      ctx.fillText('Personas', personasColCenterX, hh);
      ctx.textAlign = 'left';
      ctx.restore();

      // group tableData by departamento
      const groupMap = {};
      for (let i = 0; i < tableData.length; i++) {
        const r = tableData[i] || {};
        const dep = String(r.departamento || '').trim();
        const muni = String(r.municipio || '').trim();
        const totalHog = parseInt(String(r.totalHogares || '0').replace(/\D/g, ''), 10) || 0;
        const totalPers = parseInt(String(r.totalPersonas || '0').replace(/\D/g, ''), 10) || 0;
        
        if (!groupMap[dep]) groupMap[dep] = { departamento: dep, municipios: [] };
        groupMap[dep].municipios.push({ municipio: muni, totalHogares: totalHog, totalPersonas: totalPers });
      }

      // convert to array and calculate totals, then sort by total hogares desc
      const grouped = Object.keys(groupMap)
        .map(k => ({
          departamento: groupMap[k].departamento,
          municipios: groupMap[k].municipios.sort((a, b) => b.totalHogares - a.totalHogares),
          totalPersonas: groupMap[k].municipios.reduce((sum, m) => sum + m.totalPersonas, 0),
          totalHogares: groupMap[k].municipios.reduce((sum, m) => sum + m.totalHogares, 0)
        }))
        .sort((a, b) => b.totalHogares - a.totalHogares);

      // show only first 5 departamentos
      const displayGroups = grouped.slice(0, 5);

      // rows (on right) — group by departamento
      let ry = tableY + headerH;
      const baseRowH = rowH;
      const rowFontSize = opts.tableRowSize || 16;
      const rowFont = `${rowFontSize}px Inter, Arial, sans-serif`;
      const rowFontBold = `bold ${rowFontSize}px Inter, Arial, sans-serif`;
      ctx.font = rowFont;
      
      for (let gi = 0; gi < displayGroups.length; gi++) {
        const group = displayGroups[gi];
        const municipios = group.municipios || [];
        const numMunicipios = municipios.length;
        const totalRowGap = 10;
        const totalRowCount = Math.max(1, numMunicipios) + 1; // subtotal row
        const blockH = baseRowH * totalRowCount + totalRowGap;

        // background for the whole block (white)
        ctx.fillStyle = opts.tableRowBg || '#ffffff';
        ctx.fillRect(tableX, ry, tableAreaW, blockH);

        // departamento: draw once, vertically centered across all municipio rows
        ctx.fillStyle = opts.tableRowColor || '#0f172a';
        ctx.textBaseline = 'middle';
        ctx.font = rowFont;
        ctx.textAlign = 'left';
        const depCenterY = ry + Math.round(blockH / 2);
        
        // truncate departamento if needed
        let depDisplay = group.departamento;
        while (ctx.measureText(depDisplay).width > col1R - depPadding * 2 && depDisplay.length > 0) {
          depDisplay = depDisplay.slice(0, -1);
        }
        if (depDisplay.length < group.departamento.length) {
          depDisplay = depDisplay.slice(0, Math.max(0, depDisplay.length - 3)) + '...';
        }
        
        ctx.fillText(depDisplay, tableX + depPadding, depCenterY);

        // municipios: each on its own row
        for (let mi = 0; mi < municipios.length; mi++) {
          const m = municipios[mi];
          const rowMidY = ry + mi * baseRowH + Math.round(baseRowH / 2);
          
          // municipio text
          let muniDisplay = m.municipio;
          while (ctx.measureText(muniDisplay).width > col2R - depPadding * 2 && muniDisplay.length > 0) {
            muniDisplay = muniDisplay.slice(0, -1);
          }
          if (muniDisplay.length < m.municipio.length) {
            muniDisplay = muniDisplay.slice(0, Math.max(0, muniDisplay.length - 3)) + '...';
          }
          
          ctx.fillText(muniDisplay, tableX + col1R + depPadding, rowMidY);

          // total hogares (centered)
          ctx.textAlign = 'center';
          ctx.fillText(String(m.totalHogares), hogaresColCenterX, rowMidY);
          
          // total personas (centered)
          ctx.fillText(String(m.totalPersonas), personasColCenterX, rowMidY);
          
          ctx.textAlign = 'left';
        }

        // subtotal row (below last municipio row)
        const totalRowY = ry + numMunicipios * baseRowH + totalRowGap;
        ctx.fillStyle = opts.tableTotalRowBg || '#ffffff';
        ctx.fillRect(tableX, totalRowY, tableAreaW, baseRowH);

        // label in second column
        ctx.fillStyle = opts.tableTotalLabelColor || '#0f172a';
        const totalLabelFontSize = opts.tableTotalLabelSize || rowFontSize;
        ctx.font = `bold ${totalLabelFontSize}px Inter, Arial, sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        const totalLabelX = tableX + col1R + depPadding;
        const totalMidY = totalRowY + Math.round(baseRowH / 2);
        ctx.fillText('Subtotal', totalLabelX, totalMidY);

        // subtotal values bold and centered
        ctx.font = rowFontBold;
        ctx.textAlign = 'center';
        ctx.fillText(String(group.totalHogares), hogaresColCenterX, totalMidY);
        ctx.fillText(String(group.totalPersonas), personasColCenterX, totalMidY);
        ctx.textAlign = 'left';
        ctx.font = rowFont;

        // bottom border for the block
        ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(tableX, ry + blockH - 1);
        ctx.lineTo(tableX + tableAreaW, ry + blockH - 1);
        ctx.stroke();

        ry += blockH;
      }

      // outer table border (2px) around the full table area
      try {
        const tableHeight = Math.max(1, ry - tableY);
        ctx.strokeStyle = opts.tableBorderColor || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 2;
        // outer border
        ctx.strokeRect(tableX + 1, tableY + 1, tableAreaW - 2, tableHeight - 2);

        // vertical column separators (2px) between columns
        const sep1 = Math.round(tableX + col1R);
        const sep2 = Math.round(tableX + col1R + col2R);
        const sep3 = Math.round(tableX + col1R + col2R + col3R);
        ctx.beginPath();
        ctx.moveTo(sep1 + 0.5, tableY);
        ctx.lineTo(sep1 + 0.5, tableY + tableHeight);
        ctx.moveTo(sep2 + 0.5, tableY);
        ctx.lineTo(sep2 + 0.5, tableY + tableHeight);
        ctx.moveTo(sep3 + 0.5, tableY);
        ctx.lineTo(sep3 + 0.5, tableY + tableHeight);
        ctx.stroke();
      } catch (e) { /* ignore */ }

      // --- summary cards as a vertical column to the RIGHT of the table ---
      const resumenTabla = opts.resumenTabla || props.resumenTabla || [
        { label: 'Total Antioquia (sin Medellín)', value: '11' },
        { label: 'Total recibidos fuera de Medellín', value: '38' },
        { label: 'Porcentaje en Antioquia', value: '48%' },
      ];
      const cardsGap = typeof opts.summaryCardsGap === 'number' ? opts.summaryCardsGap : 12;
      const cardHeight = typeof opts.summaryCardHeight === 'number' ? opts.summaryCardHeight : 200;
      const cardsStartY = tableY; // align top of cards with table header

      const drawSummaryCard = (idx, x, y, w, h, item) => {
        // card box (default: yellow)
        ctx.fillStyle = opts.summaryCardBg || '#e7d488ff';
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = opts.summaryCardBorder || 'rgba(15,23,42,0.06)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

        const centerX = x + Math.round(w / 2);
        const pad = 12;

        // helper: wrap text into lines that fit maxWidth
        const wrapLines = (c, text, maxWidth) => {
          const words = String(text).split(' ').filter(Boolean);
          const lines = [];
          let line = '';
          for (let i = 0; i < words.length; i++) {
            const test = line ? line + ' ' + words[i] : words[i];
            if (c.measureText(test).width > maxWidth && line) {
              lines.push(line);
              line = words[i];
            } else {
              line = test;
            }
          }
          if (line) lines.push(line);
          return lines;
        };

        // draw label: wrap and center the block inside the top area of the card
        ctx.fillStyle = opts.summaryLabelColor || '#334155';
        const labelFontSize = opts.summaryLabelSize || 26;
        ctx.font = `${labelFontSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const maxLabelW = w - pad * 2;
        const labelLines = wrapLines(ctx, String(item.label || ''), maxLabelW);
        const lineH = Math.round(labelFontSize * 1.2);
        // target center for label block (around 33% of card height)
        const labelBlockCenterY = y + Math.round(h * 0.32);
        const labelBlockH = Math.max(1, labelLines.length) * lineH;
        let labelStartY = labelBlockCenterY - Math.round(labelBlockH / 2) + Math.round(lineH / 2);
        labelLines.forEach((ln, i) => {
          ctx.fillText(ln, centerX, labelStartY + i * lineH);
        });

        // draw value: single line, centered horizontally and placed lower in card
        ctx.fillStyle = opts.summaryValueColor || '#0f172a';
        const valueFontSize = opts.summaryValueSize || 30;
        ctx.font = `bold ${valueFontSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const valueY = y + Math.round(h * 0.68);
        ctx.fillText(String(item.value || ''), centerX, valueY);
      };

      // draw column of cards
      for (let ci = 0; ci < Math.min(resumenTabla.length, 3); ci++) {
        const item = resumenTabla[ci];
        const y = cardsStartY + ci * (cardHeight + cardsGap);
        drawSummaryCard(ci, cardsX, y, cardsColumnWidth, cardHeight, item);
      }

      // wait for logos to load then resolve draw()
      Promise.all(logoPromises).then(() => resolve());
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_cuatro_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});